'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'next/navigation';
import { 
  Loader2, 
  Download, 
  FileArchive, 
  Cloud, 
  File, 
  Home,
  Image as ImageIcon,
  Video,
  Music,
  FileText,
  X,
  ChevronLeft,
  ChevronRight,
  Play,
  Maximize2
} from 'lucide-react';
import { ThemeToggle } from '@/components/ui/theme-toggle';

interface FileItem {
  id: string;
  filename: string;
  original_name: string;
  relative_path: string;
  size: number;
  mime_type?: string;
}

interface SessionInfo {
  id: string;
  total_files: number;
  uploaded_files: number;
  is_folder: boolean;
  folder_name: string | null;
  files: FileItem[];
}

// 미디어 타입 유틸리티
function getMediaType(mimeType?: string): 'image' | 'video' | 'audio' | 'other' {
  if (!mimeType) return 'other';
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  return 'other';
}

function isMediaFile(mimeType?: string): boolean {
  const type = getMediaType(mimeType);
  return type === 'image' || type === 'video';
}

/** 미리보기 UI에서 이미지처럼 다룰 수 있는 파일 (실제 이미지 + PDF 첫 페이지 렌더) */
function isViewableAsImage(file: FileItem): boolean {
  return getMediaType(file.mime_type) === 'image' || isPdfFile(file);
}

/** 모달 뷰어에 실리는 대상. 모달 files 배열과 인덱스 계산은 반드시 이 기준을 공유해야 한다. */
function isPreviewable(file: FileItem): boolean {
  return isMediaFile(file.mime_type) || isPdfFile(file);
}

/** 뷰어/모달에 표시할 전체 크기 이미지 URL */
function viewerSrc(file: FileItem): string {
  if (isPdfFile(file) || needsImageConversion(file)) return `/api/preview/${file.id}`;
  return `/api/stream/${file.id}`;
}

/** 서버가 /api/preview/로 변환해야 하는 이미지 포맷 (브라우저 직접 렌더링 불가) */
const CONVERTIBLE_IMAGE_EXTS = ['.bmp', '.heic', '.heif', '.avif', '.tif', '.tiff', '.psd'];

/** gs로 첫 페이지를 렌더하는 문서 포맷 (.ai는 PDF 호환 래퍼) */
const DOCUMENT_RENDER_EXTS = ['.pdf', '.ai'];

function hasAnyExt(file: FileItem, exts: string[]): boolean {
  const name = (file.original_name || '').toLowerCase();
  return exts.some(e => name.endsWith(e));
}

/** 브라우저가 직접 렌더링할 수 없는 이미지 포맷 (변환 필요) */
function needsImageConversion(file: FileItem): boolean {
  return hasAnyExt(file, CONVERTIBLE_IMAGE_EXTS) ||
         file.mime_type === 'image/vnd.adobe.photoshop';
}

/** gs로 렌더된 첫 페이지를 이미지처럼 보여줄 수 있는 문서 */
function isPdfFile(file: FileItem): boolean {
  return file.mime_type === 'application/pdf' || hasAnyExt(file, DOCUMENT_RENDER_EXTS);
}

function getFileIcon(mimeType?: string) {
  const type = getMediaType(mimeType);
  switch (type) {
    case 'image': return ImageIcon;
    case 'video': return Video;
    case 'audio': return Music;
    default: return FileText;
  }
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatBitrate(bps: number): string {
  if (bps >= 1_000_000) return `${(bps / 1_000_000).toFixed(1)} Mbps`;
  if (bps >= 1_000) return `${(bps / 1_000).toFixed(0)} Kbps`;
  return `${bps} bps`;
}

interface MediaInfo {
  type: 'video' | 'image' | 'audio';
  format?: string;
  duration?: number;
  width?: number;
  height?: number;
  fps?: number;
  videoCodec?: string;
  audioCodec?: string;
  bitrate?: number;
}

interface ArchiveEntry {
  path: string;
  size: number;
}

interface ArchiveInfo {
  archiveType: string;
  totalFiles: number;
  files: ArchiveEntry[];
}

const ARCHIVE_EXTENSIONS = ['.zip', '.7z', '.tar', '.tar.gz', '.tgz', '.tar.bz2', '.tbz2', '.tar.xz', '.txz', '.unitypackage'];

function isUnityPackage(filename?: string): boolean {
  if (!filename) return false;
  return filename.toLowerCase().endsWith('.unitypackage');
}

function isArchiveFile(filename?: string): boolean {
  if (!filename) return false;
  const lower = filename.toLowerCase();
  return ARCHIVE_EXTENSIONS.some(ext => lower.endsWith(ext));
}

/** /api/preview/ 를 통해 미리보기 썸네일을 생성할 수 있는 파일인지 판별 */
function hasPreviewThumbnail(file: FileItem): boolean {
  const name = (file.original_name || '').toLowerCase();
  const mime = file.mime_type || '';
  if (mime.startsWith('video/')) return true;
  if (name.endsWith('.unitypackage')) return true;
  if (name.endsWith('.exe') || name.endsWith('.dll')) return true;
  if (needsImageConversion(file)) return true;
  if (isPdfFile(file)) return true;
  return false;
}

// 미디어 미리보기 모달 컴포넌트
function MediaPreviewModal({ 
  files, 
  currentIndex, 
  onClose, 
  onNavigate 
}: { 
  files: FileItem[]; 
  currentIndex: number; 
  onClose: () => void;
  onNavigate: (index: number) => void;
}) {
  const currentFile = files[currentIndex];
  const mediaType = getMediaType(currentFile.mime_type);
  const stripRef = useRef<HTMLDivElement>(null);
  const activeThumbRef = useRef<HTMLButtonElement>(null);

  // 필름 스트립용 이미지 src 결정
  // - 이미지: 원본 (브라우저가 표시 못 하는 포맷이면 preview API 폴백)
  // - 비디오: 썸네일 (원본은 너무 무거움)
  const getStripSrc = useCallback((file: FileItem) => {
    if (getMediaType(file.mime_type) === 'image' && !needsImageConversion(file)) {
      return `/api/stream/${file.id}`;
    }
    return `/api/preview/${file.id}?size=480&square=1`;
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft' && currentIndex > 0) onNavigate(currentIndex - 1);
    if (e.key === 'ArrowRight' && currentIndex < files.length - 1) onNavigate(currentIndex + 1);
  }, [currentIndex, files.length, onClose, onNavigate]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [handleKeyDown]);

  // 현재 인덱스가 변경되면 필름 스트립의 활성 항목을 가운데로 스크롤
  useEffect(() => {
    if (!activeThumbRef.current || !stripRef.current) return;
    activeThumbRef.current.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    });
  }, [currentIndex]);

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onClick={onClose}
    >
      {/* 닫기 버튼 */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 text-white/80 hover:text-white bg-black/50 rounded-full transition-colors z-10"
      >
        <X className="h-6 w-6" />
      </button>
      
      {/* 파일 정보 */}
      <div className="absolute top-4 left-4 text-white/80 text-sm bg-black/50 px-3 py-1.5 rounded-lg">
        <span>{currentFile.original_name}</span>
        <span className="mx-2">·</span>
        <span>{currentIndex + 1} / {files.length}</span>
      </div>
      
      {/* 이전 버튼 */}
      {currentIndex > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); onNavigate(currentIndex - 1); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 p-3 text-white/80 hover:text-white bg-black/50 rounded-full transition-colors"
        >
          <ChevronLeft className="h-8 w-8" />
        </button>
      )}
      
      {/* 미디어 콘텐츠 */}
      <div 
        className="max-w-[90vw] max-h-[85vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {isViewableAsImage(currentFile) && (
          <img
            src={viewerSrc(currentFile)}
            alt={currentFile.original_name}
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
          />
        )}
        {mediaType === 'video' && (
          <video
            src={`/api/stream/${currentFile.id}`}
            controls
            autoPlay
            className="max-w-full max-h-[85vh] rounded-lg"
          />
        )}
      </div>
      
      {/* 다음 버튼 */}
      {currentIndex < files.length - 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); onNavigate(currentIndex + 1); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-3 text-white/80 hover:text-white bg-black/50 rounded-full transition-colors"
        >
          <ChevronRight className="h-8 w-8" />
        </button>
      )}
      
      {/* 다운로드 버튼 (필름 스트립 위로 띄움) */}
      <a
        href={`/api/download/file/${currentFile.id}`}
        onClick={(e) => e.stopPropagation()}
        className="absolute bottom-32 right-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors shadow-lg"
      >
        <Download className="h-4 w-4" />
        <span>다운로드</span>
      </a>

      {/* 하단 필름 스트립 (스마트폰 갤러리 스타일) */}
      {files.length > 1 && (
        <div
          className="absolute bottom-0 left-0 right-0 pb-3 pt-4 bg-gradient-to-t from-black/80 via-black/50 to-transparent"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            ref={stripRef}
            className="flex items-center gap-2 overflow-x-auto px-[50%] scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {files.map((file, idx) => {
              const isActive = idx === currentIndex;
              const t = getMediaType(file.mime_type);
              // 현재 ±5 범위만 실제 로드 (주변 원본 프리로딩 효과)
              const inPreloadRange = Math.abs(idx - currentIndex) <= 5;
              return (
                <button
                  key={file.id}
                  ref={isActive ? activeThumbRef : null}
                  onClick={() => onNavigate(idx)}
                  className={`relative flex-shrink-0 overflow-hidden rounded-md bg-gray-800 transition-all duration-200 ${
                    isActive
                      ? 'h-20 w-20 ring-2 ring-white scale-105'
                      : 'h-14 w-14 opacity-60 hover:opacity-100'
                  }`}
                  aria-label={`${idx + 1}: ${file.original_name}`}
                  title={file.original_name}
                >
                  {inPreloadRange ? (
                    <img
                      src={getStripSrc(file)}
                      alt=""
                      className="h-full w-full object-cover"
                      loading="eager"
                      decoding="async"
                    />
                  ) : (
                    <div className="h-full w-full bg-gray-700" />
                  )}
                  {t === 'video' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <Play className="h-4 w-4 text-white drop-shadow" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// 썸네일 갤러리 컴포넌트
function MediaGallery({ 
  files, 
  onPreview 
}: { 
  files: FileItem[]; 
  onPreview: (index: number) => void;
}) {
  const mediaFiles = files.filter(isPreviewable);
  
  if (mediaFiles.length === 0) return null;
  
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 px-1">
        미디어 미리보기 ({mediaFiles.length}개)
      </h3>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
        {mediaFiles.map((file, index) => {
          const mediaType = getMediaType(file.mime_type);
          
          return (
            <button
              key={file.id}
              onClick={() => onPreview(index)}
              className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 group hover:ring-2 hover:ring-blue-500 transition-all"
            >
              {isViewableAsImage(file) && (
                <img
                  src={`/api/preview/${file.id}?size=480&square=1`}
                  alt={file.original_name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                  // @ts-expect-error fetchpriority is a valid HTML attribute
                  fetchpriority="low"
                />
              )}
              {mediaType === 'video' && (
                <div className="w-full h-full bg-gray-800 flex items-center justify-center relative">
                  {/* 비디오 썸네일 - 첫 프레임을 서버에서 480px 정사각으로 추출 */}
                  <img
                    src={`/api/preview/${file.id}?size=480&square=1`}
                    alt={file.original_name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                    // @ts-expect-error fetchpriority is a valid HTML attribute
                    fetchpriority="low"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <Play className="h-8 w-8 text-white" />
                  </div>
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                <Maximize2 className="h-6 w-6 text-white drop-shadow-lg" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function DownloadPage() {
  const params = useParams();
  const sessionId = params?.sessionId as string;
  
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());
  const [mediaInfo, setMediaInfo] = useState<MediaInfo | null>(null);
  const [archiveInfo, setArchiveInfo] = useState<ArchiveInfo | null>(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [unityPreviewUrl, setUnityPreviewUrl] = useState<string | null>(null);
  const [previewIconFailed, setPreviewIconFailed] = useState(false);
  const [pdfPreviewFailed, setPdfPreviewFailed] = useState(false);

  useEffect(() => {
    async function fetchSessionInfo() {
      if (!sessionId) {
        setError('세션 ID가 없습니다.');
        setIsLoading(false);
        return;
      }

      // UUID 형식 검증
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(sessionId)) {
        setError('잘못된 세션 ID 형식입니다.');
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/upload-session/${sessionId}`);
        if (!response.ok) {
          if (response.status === 404) {
            setError('세션을 찾을 수 없습니다.');
          } else {
            setError('세션 정보를 불러오는데 실패했습니다.');
          }
          setIsLoading(false);
          return;
        }

        const data = await response.json();
        setSessionInfo(data);
      } catch (err) {
        setError('네트워크 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    }

    fetchSessionInfo();
  }, [sessionId]);

  // 단일 미디어 파일의 미디어 정보 fetch
  useEffect(() => {
    if (!sessionInfo) return;
    const files = sessionInfo.files;
    if (!files || files.length !== 1) return;
    const file = files[0];
    const mt = file.mime_type || '';
    if (!mt.startsWith('video/') && !mt.startsWith('image/') && !mt.startsWith('audio/')) return;

    fetch(`/api/media-info/${file.id}`)
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setMediaInfo(data); })
      .catch(() => {});
  }, [sessionInfo]);

  // 단일 아카이브 파일의 내부 목록 fetch
  useEffect(() => {
    if (!sessionInfo) return;
    const files = sessionInfo.files;
    if (!files || files.length !== 1) return;
    const file = files[0];
    if (!isArchiveFile(file.original_name)) return;

    setArchiveLoading(true);
    fetch(`/api/archive-list/${file.id}`)
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setArchiveInfo(data); })
      .catch(() => {})
      .finally(() => setArchiveLoading(false));

    // .unitypackage인 경우 preview.png 추출 시도
    if (isUnityPackage(file.original_name)) {
      let blobUrl: string | null = null;
      fetch(`/api/unitypackage-preview/${file.id}`)
        .then(res => {
          if (res.ok) return res.blob();
          return null;
        })
        .then(blob => {
          if (blob) {
            blobUrl = URL.createObjectURL(blob);
            setUnityPreviewUrl(blobUrl);
          }
        })
        .catch(() => {});
      return () => {
        if (blobUrl) URL.revokeObjectURL(blobUrl);
      };
    }
  }, [sessionInfo]);

  // ZIP 다운로드
  const handleZipDownload = async () => {
    if (!sessionId) return;
    
    setIsDownloading(true);
    try {
      window.location.href = `/api/download/${sessionId}`;
    } finally {
      setTimeout(() => setIsDownloading(false), 2000);
    }
  };

  // 개별 파일 다운로드
  const handleFileDownload = (fileId: string) => {
    setDownloadingFiles(prev => new Set(prev).add(fileId));
    
    // 다운로드 링크 생성 및 클릭
    const link = document.createElement('a');
    link.href = `/api/download/file/${fileId}`;
    link.click();
    
    setTimeout(() => {
      setDownloadingFiles(prev => {
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    }, 2000);
  };

  // 단일 파일 바로 다운로드
  const handleSingleFileDownload = () => {
    if (!sessionInfo?.files?.[0]) return;
    handleFileDownload(sessionInfo.files[0].id);
  };

  // 미디어 프리뷰 관련 핸들러
  const mediaFiles = sessionInfo?.files?.filter(isPreviewable) || [];
  
  const handlePreview = (index: number) => {
    // 미디어 파일만 필터링된 인덱스에서 전체 파일 목록의 인덱스로 변환
    setPreviewIndex(index);
  };

  const handleClosePreview = () => {
    setPreviewIndex(null);
  };

  const handleNavigatePreview = (index: number) => {
    setPreviewIndex(index);
  };

  // 로딩 상태
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
          <p className="text-gray-600 dark:text-gray-400">파일 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950">
        <header className="sticky top-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Download className="h-6 w-6 text-blue-500" />
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                Kamibot Cloud
              </span>
            </div>
            <ThemeToggle />
          </div>
        </header>
        <main className="max-w-md mx-auto px-4 py-16">
          <div className="p-8 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-200 dark:border-blue-700 shadow-sm text-center">
            <Cloud className="h-16 w-16 text-blue-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3">
              이런, 파일을 찾을 수 없어요 😢
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">{error}</p>
            <a
              href="https://kamibot.app/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-medium rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
            >
              <Home className="h-4 w-4" />
              <span>카미봇 홈으로</span>
            </a>
          </div>
        </main>
      </div>
    );
  }

  // 세션 정보가 없는 경우
  if (!sessionInfo) {
    return null;
  }

  const totalSize = sessionInfo.files?.reduce((sum, f) => sum + f.size, 0) || 0;
  const fileCount = sessionInfo.files?.length || 0;
  const isSingleFile = fileCount === 1;
  const singleFile = sessionInfo.files?.[0];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950">
      {/* 미디어 프리뷰 모달 */}
      {previewIndex !== null && sessionInfo.files && (
        <MediaPreviewModal
          files={sessionInfo.files.filter(isPreviewable)}
          currentIndex={previewIndex}
          onClose={handleClosePreview}
          onNavigate={handleNavigatePreview}
        />
      )}

      {/* 헤더 */}
      <header className="sticky top-0 z-40 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Download className="h-6 w-6 text-blue-500" />
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              Kamibot Cloud
            </span>
          </div>
          <ThemeToggle />
        </div>
      </header>

      {/* 메인 컨텐츠 */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 space-y-6">
          {/* 파일 정보 */}
          <div className="text-center">
            {/* PDF는 하단에 큰 미리보기를 띄우므로 헤더에는 문서 아이콘을 유지 */}
            {isSingleFile && singleFile && hasPreviewThumbnail(singleFile) && !isPdfFile(singleFile) && !previewIconFailed ? (
              <div className="inline-flex items-center justify-center w-20 h-20 mb-4">
                <img
                  src={`/api/preview/${singleFile.id}`}
                  alt={singleFile.original_name}
                  className="w-16 h-16 object-contain"
                  onError={() => setPreviewIconFailed(true)}
                />
              </div>
            ) : (
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-4">
                {isSingleFile ? (
                  (() => {
                    const IconComponent = getFileIcon(singleFile?.mime_type);
                    return <IconComponent className="h-8 w-8 text-blue-600 dark:text-blue-400" />;
                  })()
                ) : (
                  <FileArchive className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                )}
              </div>
            )}
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {isSingleFile 
                ? singleFile?.original_name 
                : (sessionInfo.folder_name || '파일 다운로드')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              {isSingleFile 
                ? formatFileSize(singleFile?.size || 0)
                : `${fileCount}개 파일 · ${formatFileSize(totalSize)}`}
            </p>
            {/* 미디어 정보 표시 */}
            {isSingleFile && mediaInfo && (
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1 flex items-center justify-center gap-1.5 flex-wrap">
                {mediaInfo.width && mediaInfo.height && (
                  <span>{mediaInfo.width}×{mediaInfo.height}</span>
                )}
                {mediaInfo.videoCodec && (
                  <><span className="text-gray-300 dark:text-gray-600">·</span><span>{mediaInfo.videoCodec}</span></>
                )}
                {mediaInfo.audioCodec && mediaInfo.type === 'video' && (
                  <><span className="text-gray-300 dark:text-gray-600">·</span><span>{mediaInfo.audioCodec}</span></>
                )}
                {mediaInfo.fps && (
                  <><span className="text-gray-300 dark:text-gray-600">·</span><span>{mediaInfo.fps}fps</span></>
                )}
                {mediaInfo.duration && (
                  <><span className="text-gray-300 dark:text-gray-600">·</span><span>{formatDuration(mediaInfo.duration)}</span></>
                )}
                {mediaInfo.bitrate && (
                  <><span className="text-gray-300 dark:text-gray-600">·</span><span>{formatBitrate(mediaInfo.bitrate)}</span></>
                )}
              </p>
            )}
          </div>

          {/* 단일 미디어/문서 미리보기 */}
          {isSingleFile && singleFile && isPreviewable(singleFile) && !pdfPreviewFailed && (
            <div className="rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
              {isViewableAsImage(singleFile) && (
                <img
                  src={viewerSrc(singleFile)}
                  alt={singleFile.original_name}
                  className="w-full max-h-[70vh] object-contain cursor-zoom-in"
                  onClick={() => handlePreview(0)}
                  onError={() => { if (isPdfFile(singleFile)) setPdfPreviewFailed(true); }}
                />
              )}
              {getMediaType(singleFile.mime_type) === 'video' && (
                <video
                  src={`/api/stream/${singleFile.id}`}
                  controls
                  className="w-full max-h-96"
                />
              )}
            </div>
          )}

          {/* .unitypackage 미리보기 이미지 */}
          {isSingleFile && singleFile && isUnityPackage(singleFile.original_name) && unityPreviewUrl && (
            <div className="rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center p-4">
              <img
                src={unityPreviewUrl}
                alt="Unity Package Preview"
                className="max-h-64 object-contain rounded"
              />
            </div>
          )}

          {/* 아카이브 파일 내부 목록 (단일 아카이브 파일일 경우) */}
          {isSingleFile && singleFile && isArchiveFile(singleFile.original_name) && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  포함된 파일
                </span>
                {archiveInfo && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {archiveInfo.totalFiles}개 파일
                  </span>
                )}
              </div>
              {archiveLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                  <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">목록을 불러오는 중...</span>
                </div>
              ) : archiveInfo && archiveInfo.files.length > 0 ? (
                <ul className="divide-y divide-gray-200 dark:divide-gray-700 max-h-80 overflow-y-auto">
                  {archiveInfo.files.map((entry, idx) => (
                    <li key={idx} className="px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <div className="w-8 h-8 rounded bg-gray-100 dark:bg-gray-600 flex items-center justify-center flex-shrink-0">
                        <FileText className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 dark:text-gray-100 truncate" title={entry.path}>
                          {entry.path}
                        </p>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                        {formatFileSize(entry.size)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : !archiveLoading && archiveInfo ? (
                <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  빈 아카이브입니다
                </div>
              ) : null}
            </div>
          )}

          {/* 미디어 갤러리 (여러 파일일 경우) */}
          {!isSingleFile && mediaFiles.length > 0 && (
            <MediaGallery 
              files={sessionInfo.files || []} 
              onPreview={(index) => handlePreview(index)}
            />
          )}

          {/* 파일 목록 (여러 파일일 경우) */}
          {!isSingleFile && sessionInfo.files && sessionInfo.files.length > 0 && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  포함된 파일
                </span>
              </div>
              <ul className="divide-y divide-gray-200 dark:divide-gray-700 max-h-80 overflow-y-auto">
                {sessionInfo.files.map((file) => {
                  const IconComponent = getFileIcon(file.mime_type);
                  const isFileDownloading = downloadingFiles.has(file.id);
                  
                  return (
                    <li key={file.id} className="px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      {/* 아이콘 또는 썸네일 */}
                      {isViewableAsImage(file) ? (
                        <button
                          onClick={() => {
                            const mediaIndex = (sessionInfo.files?.filter(isPreviewable) || []).findIndex(f => f.id === file.id);
                            if (mediaIndex !== -1) handlePreview(mediaIndex);
                          }}
                          className="w-10 h-10 rounded overflow-hidden flex-shrink-0 hover:ring-2 hover:ring-blue-500 transition-all"
                        >
                          <img
                            src={`/api/preview/${file.id}?size=240&square=1`}
                            alt={file.original_name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                            // @ts-expect-error fetchpriority is a valid HTML attribute
                            fetchpriority="low"
                          />
                        </button>
                      ) : hasPreviewThumbnail(file) ? (
                        <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-gray-600">
                          <img
                            src={`/api/preview/${file.id}?size=240&square=1`}
                            alt={file.original_name}
                            className="w-full h-full object-contain"
                            loading="lazy"
                            decoding="async"
                            // @ts-expect-error fetchpriority is a valid HTML attribute
                            fetchpriority="low"
                            onError={(e) => {
                              // 미리보기 실패 시 아이콘으로 폴백
                              const target = e.currentTarget;
                              target.style.display = 'none';
                              target.parentElement!.classList.add('flex', 'items-center', 'justify-center');
                              const icon = document.createElement('div');
                              icon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gray-500 dark:text-gray-400"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/></svg>`;
                              target.parentElement!.appendChild(icon);
                            }}
                          />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded bg-gray-100 dark:bg-gray-600 flex items-center justify-center flex-shrink-0">
                          <IconComponent className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                        </div>
                      )}
                      
                      {/* 파일 정보 */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {file.relative_path || file.original_name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                      
                      {/* 개별 다운로드 버튼 */}
                      <button
                        onClick={() => handleFileDownload(file.id)}
                        disabled={isFileDownloading}
                        className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors disabled:opacity-50"
                        title="다운로드"
                      >
                        {isFileDownloading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* 다운로드 버튼 영역 */}
          <div className="space-y-3">
            {/* 단일 파일: 바로 다운로드 */}
            {isSingleFile ? (
              <button
                onClick={handleSingleFileDownload}
                disabled={isDownloading}
                className="w-full py-4 px-6 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {isDownloading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>다운로드 준비 중...</span>
                  </>
                ) : (
                  <>
                    <Download className="h-5 w-5" />
                    <span>다운로드</span>
                  </>
                )}
              </button>
            ) : (
              /* 여러 파일: ZIP 다운로드 */
              <button
                onClick={handleZipDownload}
                disabled={isDownloading}
                className="w-full py-4 px-6 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {isDownloading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>ZIP 생성 중...</span>
                  </>
                ) : (
                  <>
                    <FileArchive className="h-5 w-5" />
                    <span>전체 ZIP 다운로드</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </main>

      {/* 푸터 */}
      <footer className="mt-auto py-6 text-center text-sm text-gray-500 dark:text-gray-400">
        <p>Powered by Kamibot</p>
      </footer>
    </div>
  );
}
